import type { GroupBy, HealthProperty, ReviewWindow } from './types';

export const GROUP_LABELS = { all: 'All hotels', country: 'Country', contact: 'Commercial contact' } as const;

export function sortProperties(
  properties: HealthProperty[],
  window: ReviewWindow,
  sort: 'name' | 'margin' | 'decline',
) {
  return [...properties].sort((a, b) => {
    if (sort !== 'name') {
      const left =
        sort === 'decline'
          ? a.windows?.[window]?.margin_change_7d_aud.value
          : a.windows?.[window]?.lpc_margin_ar_aud.value;
      const right =
        sort === 'decline'
          ? b.windows?.[window]?.margin_change_7d_aud.value
          : b.windows?.[window]?.lpc_margin_ar_aud.value;
      if (left != null && right != null && left !== right) return sort === 'decline' ? left - right : right - left;
      if (left == null && right != null) return 1;
      if (left != null && right == null) return -1;
    }
    return a.property_name.localeCompare(b.property_name);
  });
}

export function propertyGroup(property: HealthProperty, groupBy: GroupBy): string {
  if (groupBy === 'country') return property.country || 'Country unavailable';
  if (groupBy === 'contact') return property.market_manager || 'Contact unavailable';
  return 'All hotels';
}

export function filterProperties(properties: HealthProperty[], search: string, groupBy: GroupBy, group: string) {
  const term = search.trim().toLocaleLowerCase();
  return properties.filter(
    (property) =>
      (!group || propertyGroup(property, groupBy) === group) &&
      (!term ||
        [property.property_name, property.property_id, property.country, property.market_manager]
          .filter(Boolean)
          .some((value) => value!.toLocaleLowerCase().includes(term))),
  );
}

/** An incomplete aggregate remains unavailable, rather than silently summing only covered hotels. */
export function scopedMargin(properties: HealthProperty[], window: ReviewWindow): number | null {
  if (!properties.length) return null;
  const values = properties.map((property) => property.windows?.[window]?.lpc_margin_ar_aud.value);
  if (values.some((value) => typeof value !== 'number' || !Number.isFinite(value))) return null;
  return values.reduce<number>((total, value) => total + value!, 0);
}

export function marginEvidence(properties: HealthProperty[], window: ReviewWindow) {
  const observed = properties
    .map((property) => property.windows?.[window]?.lpc_margin_ar_aud)
    .map((amount) => amount?.value ?? amount?.observed_subtotal)
    .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
  return {
    total: scopedMargin(properties, window),
    observedSubtotal: observed.length ? observed.reduce((sum, value) => sum + value, 0) : null,
    contributingHotels: observed.length,
    selectedHotels: properties.length,
  };
}

export function money(value: number | null | undefined): string {
  return typeof value === 'number' && Number.isFinite(value)
    ? new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(value)
    : 'Unavailable';
}

export function number(value: number | null | undefined): string {
  return typeof value === 'number' && Number.isFinite(value) ? value.toLocaleString('en-AU') : 'Unavailable';
}

export function percent(value: number | null | undefined): string {
  return typeof value === 'number' && Number.isFinite(value)
    ? new Intl.NumberFormat('en-AU', { style: 'percent', maximumFractionDigits: 1 }).format(value)
    : 'Unavailable';
}

/** Page chat receives the financial scope currently shown, never an unrelated portfolio total. */
export function financialPageContext(
  properties: HealthProperty[],
  selected: HealthProperty | null,
  mode: 'hotel' | 'portfolio',
  window: ReviewWindow,
) {
  const scoped = mode === 'hotel' ? (selected ? [selected] : []) : properties;
  const margin = marginEvidence(scoped, window);
  return {
    financialScope: mode === 'hotel' ? 'selected_hotel' : 'filtered_portfolio',
    visibleHotelCount: scoped.length,
    lpcMarginAfterRefundsAud: margin.total,
    observedLpcMarginSubtotalAud: margin.observedSubtotal,
    marginContributingHotels: margin.contributingHotels,
    selectedHotelPerformance: mode === 'hotel' ? (selected?.windows?.[window] ?? null) : null,
  };
}
